import * as React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import Button from "@components/button";
import { useMutation } from "urql";
import { ResetPassword } from "@graphql/mutations/auth.mutation";
import {
  ResetPasswordMutation,
  ResetPasswordMutationVariables,
} from "@graphql/types";
import { useRouter } from "next/router";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

type Props = {
  phoneNumber: string;
};

type Inputs = {
  password: string;
  otp: string;
};

const schema = yup
  .object({
    otp: yup
      .string()
      .trim("Please remove leading and trailing spaces")
      .required("Please input the otp sent to your pohne number"),
    password: yup
      .string()
      .min(6, "Please use a passowrd of at least 6 characters")
      .required("Please input your password"),
  })
  .required();

const ResetPasswordForm: React.FC<Props> = (props) => {
  const [resetResult, reset] = useMutation<
    ResetPasswordMutation,
    ResetPasswordMutationVariables
  >(ResetPassword);

  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<Inputs>({ mode: "onChange", resolver: yupResolver(schema) });

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    reset({ otp: data.otp, password: data.password, phone: props.phoneNumber })
      .then((res) => {
        if (res.data?.resetPassword) {
          router.push("/login");
          toast.success(
            <p className="toast">Please sign in with new password.</p>
          );
        }
        if (res.error) {
          toast.error(<p className="toast">{res.error.message}</p>);
        }
      })
      .catch((error) => toast.error(<p className="toast">{error.message}</p>));
  };

  return (
    <div className="max-w-lg -mb-12 px-4 w-full">
      <div className="px-4 w-full">
        <h1 className="font-libre text-2xl mb-3">Reset Password</h1>
        <p className="text-base font-light font-sfpro">
          An OTP has been sent to your Whatsapp Account.
        </p>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col justify-start items-start gap-6 mt-8 xs:mt-12 w-full"
        >
          <div className="flex flex-col justify-start items-start gap-0 w-full">
            <label
              htmlFor="otp"
              className="text-sm font-sfpro text-inawo-grey-200"
            >
              OTP
            </label>
            <input
              type="text"
              placeholder=""
              className="border-0 border-b text-base py-3 pl-0 border-inawo-grey-300 w-full focus:ring-0 focus:border-inawo-green transition"
              {...register("otp")}
            />{" "}
            {errors.otp?.message && (
              <p className="text-red-600 font-sfpro text-sm">
                {errors?.otp?.message}
              </p>
            )}
          </div>
          <div className="flex flex-col justify-start items-start gap-0 w-full">
            <label
              htmlFor="password"
              className="text-sm font-sfpro text-inawo-grey-200"
            >
              New Password (min 6 characters)
            </label>
            <input
              type="password"
              placeholder=""
              className="border-0 border-b text-base py-3 pl-0 border-inawo-grey-300 w-full focus:ring-0 focus:border-inawo-green transition"
              {...register("password")}
            />{" "}
            {errors.password?.message && (
              <p className="text-red-600 font-sfpro text-sm">
                {errors?.password?.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Button
              loader={resetResult.fetching}
              disabled={
                resetResult.fetching ||
                isSubmitting ||
                !isValid ||
                props.phoneNumber === ""
              }
              type="submit"
              label="Reset Password"
            >
              Reset Password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
